/** @jsxImportSource @emotion/react */
import React from "react";
import {
    CSSectionCard, CSSectionBody,
    CSFormRow, CSFormGroup, CSLabel, CSRequired, CSInput, CSSelect, CSTextarea,
    CSActionBar, CSPrimaryBtn, CSSecondaryBtn,
    CSFieldError,
} from "./CervicalScreeningStyles";

import {
    SCREENING_METHODS,
    SCREENING_RESULTS,
    TREATMENT_OPTIONS,
    HPV_VACCINATION_STATUSES,
    HPV_VACCINATION_DOSES,
    HIV_STATUS_OPTIONS,
} from "./hooks/useCervicalScreening";
import Form from "react-bootstrap/Form";
import Label from "../../../Utils/FormInputs/Label";
import TextField from "../../../Utils/FormInputs/TextField";
import SelectInput from "../../../Utils/FormInputs/SelectInput";
import { ResizableTextarea } from "../../../Utils/FormInputs/Textarea";

/* ═══════════════════════════════════════════════════════════════
   CervicalScreeningForm — Screening form for Task 19:
   Cervical Cancer Screening.
   Sections: Screening Info, Result, Treatment, HPV/HIV, Follow-up
   ═══════════════════════════════════════════════════════════════ */

const formatDate = function (dateStr) {
    if (!dateStr) return "—";
    try {
        const d = new Date(dateStr);
        return d.toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" });
    } catch (e) {
        return dateStr;
    }
};

const getResultLabel = function (value) {
    const found = SCREENING_RESULTS.find(function (r) { return r.value === value; });
    return found ? found.label : value;
};

export const CervicalScreeningForm = function (props) {
    const cs = props.cs;
    const form = cs.form;
    const errors = cs.validationErrors;

    const _React$useState = React.useState(false);
    const submitAttempted = _React$useState[0];
    const setSubmitAttempted = _React$useState[1];

    const onChange = function (field) {
        return function (e) {
            cs.handleChange(field, e.target.value);
        };
    };

    return (
        <div>
            {/* ═══ Single Form Page ═══ */}
            <CSSectionCard>
                <CSSectionBody style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    
                    <CSFormRow cols="1fr 1fr 1fr">
                        {/* Screening Date */}
                        <Form.Group>
                            <Label name='Screening Date'/>
                            <TextField
                                type="date"
                                value={form.screening_date}
                                onChange={onChange("screening_date")}
                            />
                            {(submitAttempted && errors.screening_date) && <CSFieldError>{errors.screening_date}</CSFieldError>}
                        </Form.Group>

                        {/* Screening Method */}
                        <Form.Group>
                            <Label name='Screening Method'/>
                            <SelectInput
                                value={form.screening_method}
                                onChange={onChange("screening_method")}
                                name="screening_method"
                                options={SCREENING_METHODS.map(item=>(
                                    {value:item.value, text:item.label}
                                ))}
                                defaultOption="— Select Method —"
                            />
                               
                            {(submitAttempted && errors.screening_method) && <CSFieldError>{errors.screening_method}</CSFieldError>}
                        </Form.Group>

                        {/* HIV Status */}
                        <Form.Group>
                            <Label name='HIV Status at Screening'/>
                            <SelectInput
                                value={form.hiv_status_at_screening}
                                onChange={onChange("hiv_status_at_screening")}
                                name="hiv_status_at_screening"
                                options={HIV_STATUS_OPTIONS.map(item=>(
                                    {value:item.value, text:item.label}
                                ))}
                                defaultOption="— Select —"
                            />
                                
                        </Form.Group>
                    </CSFormRow>

                    <CSFormRow cols={form.hpv_vaccination_status === "vaccinated" ? "1fr 1fr 1fr" : "1fr 1fr"}>
                        {/* Screening Result */}
                        <Form.Group>
                            <Label name='Screening Result'/>
                            <SelectInput
                                value={form.result}
                                onChange={onChange("result")}
                                name="result"
                                options={SCREENING_RESULTS.map(item=>(
                                    {value:item.value, text:item.label}
                                ))}
                                defaultOption="— Select Result —"
                            />
                              
                            {(submitAttempted && errors.result) && <CSFieldError>{errors.result}</CSFieldError>}
                        </Form.Group>

                        {/* HPV Vaccination Status */}
                        <Form.Group>
                            <Label name='HPV Vaccination Status'/>
                            <SelectInput
                                value={form.hpv_vaccination_status}
                                onChange={onChange("hpv_vaccination_status")}
                                name="hpv_vaccination_status"
                                options={HPV_VACCINATION_STATUSES.map(item=>(
                                    {value:item.value, text:item.label}
                                ))}
                                defaultOption="— Select —"
                            />
                        </Form.Group>

                        {/* HPV Vaccination Doses */}
                        {form.hpv_vaccination_status === "vaccinated" && (
                            <Form.Group>
                                <Label name='Number of Doses Received'/>
                                <SelectInput
                                    value={form.hpv_vaccination_doses}
                                    onChange={onChange("hpv_vaccination_doses")}
                                    name="hpv_vaccination_doses"
                                    options={HPV_VACCINATION_DOSES.map(item=>(
                                        {value:item.value, text:item.label}
                                    ))}
                                    defaultOption="— Select —"
                                />
                                    
                            </Form.Group>
                        )}
                    </CSFormRow>

                    {form.result && form.result !== "negative" && (
                        <CSFormRow cols={(form.treatment_given !== "none" && form.treatment_given === "referral") ? "1fr 1fr 1fr" : (form.treatment_given !== "none" && form.treatment_given ? "1fr 1fr" : "1fr")}>
                            <Form.Group>
                                <Label name='Select treatment provided'/>
                                <SelectInput
                                    value={form.treatment_given}
                                    onChange={onChange("treatment_given")}
                                    name="treatment_given"
                                    options={TREATMENT_OPTIONS.map(item=>(
                                        {value:item.value, text:item.label}
                                    ))}
                                    defaultOption="— Select Treatment —"
                                />
                                   
                                       
                                {(submitAttempted && errors.treatment_given) && <CSFieldError>{errors.treatment_given}</CSFieldError>}
                            </Form.Group>

                            {form.treatment_given !== "none" && form.treatment_given && (
                                <Form.Group>
                                    <Label name='Treatment Date'/>
                                    <TextField
                                        type="date"
                                        value={form.treatment_date}
                                        onChange={onChange("treatment_date")}
                                    />
                                    {(submitAttempted && errors.treatment_date) && <CSFieldError>{errors.treatment_date}</CSFieldError>}
                                </Form.Group>
                            )}

                            {form.treatment_given === "referral" && (
                                <Form.Group>
                                    <Label name='Referral Facility'/>
                                    <TextField
                                        type="text"
                                        placeholder="Name of referral facility"
                                        value={form.referral_facility}
                                        onChange={onChange("referral_facility")}
                                    />
                                    {(submitAttempted && errors.referral_facility) && <CSFieldError>{errors.referral_facility}</CSFieldError>}
                                </Form.Group>
                            )}
                        </CSFormRow>
                    )}

                    {form.result && (
                        <CSFormRow cols="1fr">
                            <Form.Group>
                                <Label name='Next Screening Date (auto-calc/override)'/>
                                <TextField
                                    type="date"
                                    value={form.next_screening_date}
                                    onChange={onChange("next_screening_date")}
                                />
                            </Form.Group>
                        </CSFormRow>
                    )}

                    <CSFormRow cols="1fr">
                        <Form.Group>
                            <Label name='Clinical Notes / Observations'/>
                            <ResizableTextarea
                                placeholder="Describe findings, patient counselling provided, any additional observations..."
                                value={form.notes}
                                onChange={onChange("notes")}
                                rows={2}
                            />
                        </Form.Group>
                    </CSFormRow>

                </CSSectionBody>
            </CSSectionCard>

            {/* ═══ Action Bar ═══ */}
            <CSActionBar>
                {cs.editingRecord && (
                    <CSSecondaryBtn onClick={function() { cs.cancelEdit(); setSubmitAttempted(false); }}>
                        Cancel Edit
                    </CSSecondaryBtn>
                )}
                <CSSecondaryBtn onClick={function() { cs.resetForm(); setSubmitAttempted(false); }}>
                    Reset Form
                </CSSecondaryBtn>
                <button
                    className="sms-btn btn"
                    disabled={cs.submitting}
                    onClick={function() { setSubmitAttempted(true); cs.handleSubmit(); }}
                >
                    {cs.submitting
                        ? "Saving..."
                        : cs.editingRecord
                            ? "Update Screening"
                            : "Save Screening"
                    }
                </button>
            </CSActionBar>
        </div>
    );
};
